/**
 * Centralized Media Service
 *
 * Provides utilities for handling media file uploads via FormData.
 * Use this instead of direct uploads to /api/media/files/ to avoid S3 permission issues.
 *
 * Pattern: Store media locally during editing, upload with entity on save.
 */

import { axiosInstance } from './api';

/**
 * Build FormData from entity data + local media files
 *
 * @param {Object} entityData - The entity fields (title, content, etc.)
 * @param {Array} localMediaFiles - Array of {file, name, mediaType} objects
 * @param {Object} options - {mediaFieldName: 'media_files'}
 * @returns {FormData}
 *
 * @example
 * const formData = buildFormData(
 *   { title: 'My Letter', message: 'Hello!' },
 *   [{ file: audioBlob, name: 'recording.webm' }]
 * );
 */
export const buildFormData = (entityData, localMediaFiles = [], options = {}) => {
  const formData = new FormData();
  // Use 'media_uploads' for file uploads to avoid conflict with serializer's 'media_files' field
  const mediaFieldName = options.mediaFieldName || 'media_uploads';

  // Add entity fields
  Object.entries(entityData).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      // Check arrays FIRST (arrays are objects in JS)
      if (Array.isArray(value)) {
        // For arrays of simple values (strings, UUIDs), append each separately
        // DRF expects repeated keys for ManyToMany fields
        value.forEach(item => formData.append(key, item));
      } else if (value instanceof File || value instanceof Blob) {
        // Files/Blobs go directly
        formData.append(key, value);
      } else if (typeof value === 'object') {
        // Other objects get JSON-encoded
        formData.append(key, JSON.stringify(value));
      } else {
        // Primitives (strings, numbers, booleans)
        formData.append(key, value);
      }
    }
  });

  // Add local media files
  localMediaFiles.forEach(media => {
    if (media.file instanceof File || media.file instanceof Blob) {
      const filename = media.name || media.file.name || `media-${Date.now()}`;
      formData.append(mediaFieldName, media.file, filename);
    }
  });

  return formData;
};

/**
 * Create entity with media files in single request
 *
 * @param {string} endpoint - API endpoint (e.g., '/timecapsule/capsules/')
 * @param {Object} entityData - Entity fields
 * @param {Array} localMediaFiles - Local files to upload
 * @returns {Promise<Object>} Created entity data
 *
 * @example
 * const capsule = await createWithMedia(
 *   '/timecapsule/capsules/',
 *   { recipient: 123, message: 'Hello!' },
 *   localMediaFiles
 * );
 */
export const createWithMedia = async (endpoint, entityData, localMediaFiles = [], { onProgress } = {}) => {
  const formData = buildFormData(entityData, localMediaFiles);

  const response = await axiosInstance.post(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1))) : undefined,
  });

  return response.data;
};

/**
 * Update entity with media files
 *
 * @param {string} endpoint - API endpoint with ID (e.g., '/timecapsule/capsules/123/')
 * @param {Object} entityData - Entity fields to update
 * @param {Array} localMediaFiles - New local files to upload
 * @returns {Promise<Object>} Updated entity data
 */
export const updateWithMedia = async (endpoint, entityData, localMediaFiles = [], { onProgress } = {}) => {
  const formData = buildFormData(entityData, localMediaFiles);

  const response = await axiosInstance.patch(endpoint, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1))) : undefined,
  });

  return response.data;
};

/**
 * Contribute media files to an existing life event (for family members)
 *
 * @param {string} eventId - The life event ID
 * @param {Array<File>} files - Array of File objects to upload
 * @returns {Promise<Object>} Response with contributed_media array
 *
 * @example
 * const result = await contributeMedia('event-123', [file1, file2]);
 */
export const contributeMedia = async (eventId, files, { onProgress } = {}) => {
  const formData = new FormData();

  files.forEach(file => {
    formData.append('media_uploads', file);
  });

  const response = await axiosInstance.post(
    `/lifeevent/events/${eventId}/contribute_media/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1))) : undefined,
    }
  );

  return response.data;
};

/**
 * Get media file URL (handles both local previews and server URLs)
 *
 * @param {Object} mediaFile - Media file object
 * @returns {string|null} URL to display the media
 */
export const getMediaUrl = (mediaFile) => {
  if (!mediaFile) return null;

  // Local preview (not yet uploaded)
  if (mediaFile.isLocal && mediaFile.preview) {
    return mediaFile.preview;
  }

  // Server URL - check various field names
  if (mediaFile.file_url) return mediaFile.file_url;
  if (mediaFile.file) return mediaFile.file;
  if (mediaFile.url) return mediaFile.url;

  return null;
};

/**
 * Check if media file is local (needs upload on save)
 *
 * @param {Object} mediaFile - Media file object
 * @returns {boolean}
 */
export const isLocalMedia = (mediaFile) => {
  return mediaFile?.isLocal === true;
};

/**
 * Separate local vs server media files
 *
 * @param {Array} mediaFiles - Mixed array of local and server media
 * @returns {{local: Array, server: Array}}
 *
 * @example
 * const { local, server } = partitionMedia(mediaFiles);
 * // local: files that need uploading
 * // server: files already on server (have IDs)
 */
export const partitionMedia = (mediaFiles = []) => {
  const local = mediaFiles.filter(f => isLocalMedia(f));
  const server = mediaFiles.filter(f => !isLocalMedia(f));
  return { local, server };
};

/**
 * Create a local media file object from captured media
 *
 * @param {Object} mediaData - Data from media capture (file, preview, type, duration)
 * @returns {Object} Local media file object with isLocal flag
 *
 * @example
 * const localFile = createLocalMediaFile({
 *   file: blob,
 *   preview: URL.createObjectURL(blob),
 *   type: 'audio',
 *   duration: 30
 * });
 */
export const createLocalMediaFile = (mediaData) => {
  return {
    id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    file: mediaData.file,
    preview: mediaData.preview,
    name: mediaData.file?.name || mediaData.name || `recording-${Date.now()}.webm`,
    media_type: mediaData.type || mediaData.media_type || 'audio',
    file_size: mediaData.file?.size || 0,
    duration: mediaData.duration,
    isLocal: true
  };
};

/**
 * Detect media type from file
 *
 * @param {File|Blob} file - The file to check
 * @returns {string} Media type: 'image', 'video', 'audio', or 'document'
 */
export const detectMediaType = (file) => {
  if (!file) return 'document';

  const filename = (file.name || '').toLowerCase();
  const mimeType = file.type || '';

  // Check MIME type first
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';

  // Fall back to extension
  const ext = filename.split('.').pop();

  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg'].includes(ext)) {
    return 'image';
  }
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'm4v', 'webm'].includes(ext)) {
    // Special case: webm can be audio
    if (ext === 'webm' && filename.includes('audio')) {
      return 'audio';
    }
    return 'video';
  }
  if (['mp3', 'wav', 'm4a', 'ogg', 'aac', 'flac', 'wma'].includes(ext)) {
    return 'audio';
  }

  return 'document';
};
